import {
  BarChart3,
  Bot,
  Building2,
  Database,
  KeyRound,
  LayoutDashboard,
  LayoutTemplate,
  MessageSquare,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Capability required to see this section, from /v1/auth/me. */
  permission?: string;
}

/**
 * Tenant-surface sections — everything scoped to whichever workspace is
 * currently active. Carried over from the Alpine dashboard but now real
 * routes with real URLs. Visibility is driven by server-issued permissions,
 * so the menu can never offer something the API would refuse.
 */
export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard },
  { to: '/bots', label: 'Assistants', icon: Bot, permission: 'bots:read' },
  { to: '/sources', label: 'Knowledge', icon: Database, permission: 'sources:read' },
  { to: '/tools', label: 'Tools', icon: Wrench, permission: 'tools:read' },
  { to: '/tokens', label: 'API keys', icon: KeyRound, permission: 'tokens:read' },
  { to: '/chats', label: 'Conversations', icon: MessageSquare, permission: 'chats:read' },
  { to: '/users', label: 'Team', icon: Users, permission: 'users:read' },
  { to: '/usage', label: 'Usage', icon: BarChart3, permission: 'stats:read' },
];

/**
 * System-level sections — nothing here belongs to any one tenant, so they sit
 * at the bottom of the sidebar, separate from the workspace-scoped list above,
 * and stay reachable with zero workspaces in the system. "Admins" is gated on
 * `users:manage_staff` rather than `users:read`: creating an OWNER/ADMIN
 * account is OWNER-only, so nobody else gets the entry point.
 */
export const NAV_ITEMS_SYSTEM: NavItem[] = [
  { to: '/tenants', label: 'Workspaces', icon: Building2, permission: 'tenants:read' },
  // Gated on 'bots:write' rather than a dedicated permission — blueprint
  // curation shares the exact same OWNER/ADMIN-only boundary as creating a
  // bot directly (see app/services/blueprints.py's module docstring).
  // Business-tenant roles never see this entry, not even to browse.
  { to: '/blueprints', label: 'Blueprints', icon: LayoutTemplate, permission: 'bots:write' },
  { to: '/admins', label: 'Admins', icon: ShieldCheck, permission: 'users:manage_staff' },
  // The cross-tenant audit log — reading it is OWNER-only (`platform:admin`).
  // ADMIN has `platform:support` (can view a single tenant's trail via the
  // admin API) but not this standalone system-wide view.
  { to: '/audit-log', label: 'Audit log', icon: ScrollText, permission: 'platform:admin' },
  { to: '/settings', label: 'Settings', icon: Settings },
];

/**
 * The nav the current role should see: an item with no `permission` is always
 * shown, one with a `permission` only if `can(permission)` is true. The
 * Sidebar renders from this so the menu can never offer a section the API
 * would refuse.
 */
export function filterNavItems(items: NavItem[], can: (permission: string) => boolean): NavItem[] {
  return items.filter((item) => !item.permission || can(item.permission));
}
